<template>
  <section class="mt-12 border-b border-neutral-300 pb-6">
    <h3 class="mb-2 text-[1.3rem] text-neutral-650">{{ wt('projectSubmission', 'planningStart', 'title') }}</h3>
    <p class="mb-6 text-sm text-neutral-450">{{ wt('projectSubmission', 'planningStart', 'description') }}</p>

    <div class="grid grid-cols-1 gap-x-10 md:grid-cols-2">
      <!-- Year -->
      <div class="flex flex-col">
        <label class="core-form-label core-form-label--required" for="ps-year">
          {{ wt('projectSubmission', 'planningStart', 'year') }}
        </label>
        <NumberInput
          id="ps-year"
          v-model="form.year"
          :maxLength="4"
          :placeholder="wt('projectSubmission', 'planningStart', 'yearPlaceholder')"
          class="core-form-input core-form-input--full"
          :class="{ 'core-form-error': errors['planningStart.year'] }" />
        <p v-if="errors['planningStart.year']" class="text-sm text-red-400 mb-4">
          {{ errors['planningStart.year'] }}
        </p>
      </div>

      <!-- Construction Costs -->
      <div class="flex flex-col">
        <label class="core-form-label" for="ps-construction-costs">
          {{ wt('projectSubmission', 'planningStart', 'constructionCosts') }}
        </label>
        <div class="flex items-center">
          <NumberInput
            id="ps-construction-costs"
            v-model="form.constructionCosts"
            :formatBigNumber="true"
            :placeholder="wt('projectSubmission', 'planningStart', 'constructionCostsPlaceholder')"
            class="core-form-input core-form-input--full text-right" />
          <span class="mb-7 ml-2 shrink-0 text-lg text-neutral-550">{{ currency }}</span>
        </div>
      </div>
    </div>

    <!-- Per-role fields -->
    <div v-for="role in validRoles" :key="role" class="mt-6">
      <h4 class="core-form-role-title text-[1.2rem]">{{ wtGeneral('roleParts', role) }}</h4>

      <div class="grid grid-cols-1 gap-x-10 md:grid-cols-2">
        <!-- Effort-determining costs -->
        <div class="flex flex-col">
          <label class="core-form-label core-form-label--required">
            {{ wt('projectSubmission', 'planningStart', 'effortDeterminingCosts') }}
          </label>
          <div class="flex items-center">
            <NumberInput
              v-model="form.roleData[role].effortDeterminingCosts"
              :formatBigNumber="true"
              :placeholder="wt('projectSubmission', 'planningStart', 'effortDeterminingCostsPlaceholder')"
              class="core-form-input core-form-input--full text-right"
              :class="{ 'core-form-error': errors[`planningStart.effortDeterminingCosts.${role}`] }" />
            <span class="mb-7 ml-2 shrink-0 text-lg text-neutral-550">{{ currency }}</span>
          </div>
          <p v-if="errors[`planningStart.effortDeterminingCosts.${role}`]" class="text-sm text-red-400 mb-4">
            {{ errors[`planningStart.effortDeterminingCosts.${role}`] }}
          </p>
        </div>

        <!-- Service share -->
        <div class="flex flex-col">
          <label class="core-form-label">
            {{ wt('projectSubmission', 'planningStart', 'serviceShare') }}
          </label>
          <div class="flex items-center">
            <NumberInput
              v-model="form.roleData[role].serviceShare"
              :placeholder="wt('projectSubmission', 'planningStart', 'serviceSharePlaceholder')"
              class="core-form-input core-form-input--full text-right"
              :class="{ 'core-form-error': errors[`planningStart.serviceShare.${role}`] }" />
            <span class="mb-7 ml-2 shrink-0 text-lg text-neutral-550">%</span>
          </div>
          <p v-if="errors[`planningStart.serviceShare.${role}`]" class="text-sm text-red-400 mb-4">
            {{ errors[`planningStart.serviceShare.${role}`] }}
          </p>
        </div>

        <!-- Offered hours -->
        <div class="flex flex-col">
          <label class="core-form-label">
            {{ wt('projectSubmission', 'planningStart', 'offeredHours') }}
          </label>
          <div class="flex items-center">
            <NumberInput
              v-model="form.roleData[role].offeredHours"
              :formatBigNumber="true"
              :placeholder="wt('projectSubmission', 'planningStart', 'offeredHoursPlaceholder')"
              class="core-form-input core-form-input--full text-right" />
            <span class="mb-7 ml-2 shrink-0 text-lg text-neutral-550">h</span>
          </div>
        </div>
      </div>

      <div class="flex flex-col">
        <label class="core-form-label">
          {{ getDifficultyLabel(role) }}
        </label>
        <RangeInput
          :modelValue="form.roleData[role].difficultyFactor ?? 1"
          :min="getDifficultyOptions(role)[0]"
          :max="getDifficultyOptions(role)[getDifficultyOptions(role).length - 1]"
          :step="0.1"
          :class="{ 'core-form-error': errors[`planningStart.difficultyFactor.${role}`] }"
          @update:modelValue="form.roleData[role].difficultyFactor = $event" />
        <div class="mb-4 -mt-2 flex justify-between text-sm text-neutral-450 lg:-mt-6">
          <span v-for="opt in getDifficultyOptions(role)" :key="opt">{{ opt }}</span>
        </div>
        <p v-if="errors[`planningStart.difficultyFactor.${role}`]" class="mb-4 text-sm text-red-400">
          {{ errors[`planningStart.difficultyFactor.${role}`] }}
        </p>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue'
import NumberInput from '@/components/partials/inputs/NumberInput.vue'
import RangeInput from '@/components/partials/inputs/RangeInput.vue'
import { useWidgetText } from '@/helpers/useWidgetText'
import { getCurrencyForCountry } from '@/helpers/format'
import { getSubmissionDifficultyLabel, getSubmissionDifficultyOptions } from '@/helpers/submission-difficulty'

const { wt } = useWidgetText('widget-texts-project-summary')
const { wt: wtGeneral } = useWidgetText('widget-texts-general')

interface PlanningStartRoleData {
  difficultyFactor: number | null
  effortDeterminingCosts: number | null
  serviceShare: number | null
  offeredHours: number | null
}

interface PlanningStartForm {
  year: number | null
  constructionCosts: number | null
  roleData: Record<string, PlanningStartRoleData>
}

const form = defineModel('form', { type: Object as PropType<PlanningStartForm>, required: true })

const props = defineProps<{
  validRoles: string[]
  country: string
  errors: Record<string, string>
}>()

const currency = computed(() => getCurrencyForCountry(props.country))

function getDifficultyOptions(role: string): number[] {
  return getSubmissionDifficultyOptions(role)
}

function getDifficultyLabel(role: string): string {
  return getSubmissionDifficultyLabel(role, wt, 'planningStart')
}
</script>
